import { AppContent } from '../AppContent';
import { MetaContentValues } from '../metaContentValues';

const makingRepairs: AppContent['makingRepairs'] = {
  metaContent: {
    title: MetaContentValues.makingRepairs.title,
  },
  backLabel: 'Back to Landlord support',
  title: 'Making repairs',
  yourResponsibility:
    "It's your responsibility to make any repairs to the property and anything you've supplied inside it as part of the tenancy agreement. The property must be kept in good condition and any gas or electrical appliances and fittings must meet current safety standards.",
  asTheLandlord: {
    title: "As the landlord, you're normally responsible for:",
    listItems: [
      'the structure of your property, although freeholders are sometimes responsible for this',
      'all basins, sinks, baths, and similar fittings',
      'any heating and hot water systems, like boilers',
      'anything you damage when attempting repairs.',
    ],
    blockOfFlats:
      "If you own a block of flats, you're usually responsible for repairing common areas, like staircases. Your local council can ask you to make repairs in common areas. If a tenant damages another tenant's flat, they can also ask you to fix it.  ",
  },
  enteringPropertyAccordion: {
    label: 'Entering the rental property to make repairs',
    enterPropertyForInspection:
      "You can enter your property for inspection or to carry out repairs, but you must give your tenants 24 hours' written - this includes text and email - notice, unless it's an emergency. Your tenants have the right to be in the property while repairs are made.",
    repairRefusalTitle:
      "If repairs aren't done properly or you refuse to do them",
    tenantsCanClaim:
      'Tenants can make a claim against you in the small claims court for up to £10,000. They can also ask their local council to inspect the property under the ',
    hhsrsLinkLabel: 'Housing Health and Safety Rating System (HHSRS)',
    hhsrsLinkHref:
      'https://www.gov.uk/government/publications/housing-health-and-safety-rating-system-guidance-for-landlords-and-property-related-professionals/housing-health-and-safety-rating-system-guidance-for-landlords-and-property-related-professionals',
    seriousHazards:
      ". If the council finds any serious hazard, it must take action against you to make sure it's removed.",
    tenantsRepair:
      'Tenants can sometimes do the repairs themselves and take the cost off their rent.',
    majorRepairsTitle: 'If you need to make major repairs',
    askTenantsToMove:
      "You might have to ask your tenants to move out temporarily to make major repairs or if the property won't be fit to live in for a while. You'll need to inform your tenants of this in writing beforehand.",
    youMust: 'You must:',
    majorRepairsListItems: [
      'agree how long any works will last for',
      "confirm your tenants' right to return to the property",
      'provide details of alternative accommodation.',
    ],
    cantRepossess:
      "You can't repossess a property to carry out repairs, but you can apply for a court order to get your tenants to leave. Having alternative accommodation for them to move to will make it more likely for the court order to be successful.",
    reduceRent:
      "In some cases, your tenants might be able to reduce how much rent they pay while you carry out repairs. This is known as 'rent abatement' and depends on how disruptive your work is.",
    changeStandards:
      'These standards can change at any time. Always check with ',
    govLinkLabel: 'gov.co.uk',
    govLinkHref: 'https://www.gov.uk/renting-out-a-property/making-repairs',
    beforeStart: ' before starting anything.',
    standardsInWales: 'For the standards in Wales, please check ',
    welshGovLinkLabel: 'gov.wales',
    welshGovLinkHref:
      'https://www.gov.wales/fitness-homes-human-habitation-guidance-landlords',
    period: '.',
  },
  scotlandAccordion: {
    label: 'Making repairs - Scotland',
    recognisedStandards:
      'In Scotland, landlords must conform to two recognised standards when making repairs or maintenance. These standards are the Repairing Standards and the Tolerable Standard.',
    repairingStandardTitle: "What's the Repairing Standard?",
    basicLevel:
      "It's the basic level of repair all privately rented properties must adhere to in Scotland.",
    meetRepairStandardTitle: 'To meet the standard, all properties have to:',
    meetRepairStandardListItems: [
      'be windproof and watertight',
      'have its roof and walls in reasonable condition',
      'have all water, gas, electricity, and plumbing in working order',
      'have any fixtures, fittings, and appliances supplied by the landlord in a reasonable condition',
      'have any furnishings supplied by the landlord in a condition so they can be used properly',
      'have access to a food storage and preparation space',
      'be inspected by a qualified electrician at least once every five years.',
    ],
    commonAreas:
      'If the property has any common areas, they have to be safely accessible and useable. For tenement properties, all doors to common areas must be secure and fitted with emergency exit locks.',
    acquireConsent:
      "It's not your responsibility to carry out work on common areas if you can't get the rights or consent needed to do it. But you must take reasonable steps to get the rights or consent, but you're not at fault if it can't be done.",
    repairingGuidanceLinkLabel: 'Read the Repairing Standard guidance in full',
    repairingGuidanceLinkHref:
      'https://www.gov.scot/publications/repairing-standard-statutory-guidance-private-landlords',
    gasSafety:
      'You must also make sure your property adheres to UK-wide gas safety and health and safety at work legislation, including carrying out a Legionella risk assessment.',
    doesntMeetStandardsTitle: "If your property doesn't meet the standards",
    tenantReport:
      'And you refuse to carry out the repair work, your tenant can report you to the ',
    housingChamberLinkLabel: 'Housing and Property Chamber',
    housingChamberLinkHref:
      'https://www.mygov.scot/landlord-repairs/housing-and-property-chamber',
    period: '.',
    tolerableStandardsTitle: "What's the Tolerable Standard?",
    tolerableBasicLevel:
      'Like the Repairing Standard, the Tolerable Standard is a basic level of repair your property must meet to make it fit for a person to live in. There are some differences between the two. The Tolerable Standard is more about your property being fit to live in.',
    meetTolerableStandardTitle: 'To meet the standard, a property must:',
    meetTolerableStandardListItems: [
      'not have any damp issues',
      'be structurally stable',
      'have proper ventilation, adequate natural and artificial light, and heating',
      'have an acceptable water supply, or a sink with hot and cold water',
      'have an indoor toilet, and bath or shower as well as a sink with hot and cold water',
      'have good drainage and sewage system',
      'meet electrical safety standards',
      'have a proper entrance',
      'be fitted with a smoke and heat alarm and carbon monoxide detectors.',
    ],
    cookerSpace:
      "The property also has to have a space where a cooker can be installed by the tenant if you haven't provided one.",
    tolerableStandardLinkLabel: 'Read the Tolerable Standard guidance in full',
    tolerableStandardLinkHref:
      'https://www.mygov.scot/landlord-repairs/tolerable-standard',
    reportToHousingChamber: 'Your tenant can report you to the ',
    propertyChamberLinkLabel: 'Housing and Property Chamber',
    propertyChamberLinkHref:
      'https://www.mygov.scot/landlord-repairs/housing-and-property-chamber',
  },
  northernIrelandAccordion: {
    label: 'Making repairs - Northern Ireland',
    asALandlord:
      'As a landlord in Northern Ireland, there are things you must do by law.',
    responsibleTitle: "You're responsible for:",
    responsibleListItems: [
      'any repairs to the structure and exterior of the property, heating and hot water systems, basins, sinks, baths and other plumbing',
      'gas and electrical appliances safety',
      'the fire safety of furniture and furnishings you provide',
      'making sure the property is fit for habitation',
      'repairing and maintaining the property heating system',
      'any common areas, if there are any.',
    ],
    responsibilitiesLinkLabel: 'Landlord responsibilities in Northern Ireland',
    responsibilitiesLinkHref:
      'https://www.communities-ni.gov.uk/publications/repairs-guide-private-landlords-and-tenants',
  },
};

export default makingRepairs;
